import { L10nBase, RootContextBase } from "@skylab-shared/core";
import { FiscalDeviceModel } from "@skylab-shared/dataModel";
import { ArticlePaymentMethodObject, ArticleVatRateObject } from "../common/suz-article";
import { DocumentToFiscalize, ProductToFiscalize } from "../common/suz-helper-types";
import { SuzInternalPrintReceiptResponse, SuzInternalResponse } from "../common/suz-internal-response";
import { SuzInternalStatus } from "../common/suz-internal-status";
import { SuzService } from "../common/suz-service-interface";
import { SuzCredentials } from "../queries/suz-credentials.query-base";
import { Suz17ServiceBase } from "./suz.service-base";

export class Suz17AdapterServiceBase implements SuzService {
	#suzService: Suz17ServiceBase;
	public version = '17';

	public constructor(
		l10n: L10nBase,
		rootContext: RootContextBase,
	) {
		this.#suzService = new Suz17ServiceBase(l10n, rootContext);
	}

	public async getBaseUrlAsync(): Promise<string> {
		return this.#suzService.suzBaseUrl;
	}

	public async checkConnectionAsync(): Promise<SuzInternalResponse> {
		const result = await this.#suzService.checkConnectionAsync();
		if (result.status === SuzInternalStatus.Success) {
			return {
				type: SuzInternalStatus.Success,
			};
		} else {
			return {
				type: SuzInternalStatus.Error,
			} as SuzInternalResponse;
		}
	}

	public async getExistingPrintersNamesAsync(): Promise<SuzInternalResponse<string[]>> {
		const result = await this.#suzService.getPrinterNamesAsync();
		if (result.status === SuzInternalStatus.Success) {
			return {
				type: SuzInternalStatus.Success,
				data: result.data?.length ? result.data : undefined,
			};
		} else {
			return {
				type: SuzInternalStatus.Error,
			};
		}
	}

	public async checkPrinterConnectionAsync(printerName: string): Promise<SuzInternalResponse> {
		const result = await this.#suzService.checkPrinterConnectionAsync(printerName);
		return { type: result.status } as SuzInternalResponse;
	}

	public async getPrinterVatTableAsync(printerName: string): Promise<ArticleVatRateObject | null> {
		const result = await this.#suzService.getDeviceVatTableAsync(printerName);
		if (result.status === SuzInternalStatus.Success) {
			return result.data;
		} else {
			return null;
		}
	}

	public async getPrinterPaymentMethodsAsync(printerName: string): Promise<SuzInternalResponse<ArticlePaymentMethodObject>> {
		const result = await this.#suzService.getDevicePaymentMethodsAsync(printerName);
		if (result.status === SuzInternalStatus.Success) {
			return {type: result.status, data: result.data};
		} if (result.status === SuzInternalStatus.OperationNotExecuted) {
			return {type: result.status, suzMessages: []};
		} else {
			return { type: SuzInternalStatus.Error };
		}
	}

	public async printReceiptAsync(document: DocumentToFiscalize, products: ProductToFiscalize[], device: FiscalDeviceModel): Promise<SuzInternalPrintReceiptResponse> {
		const result = await this.#suzService.printReceiptAsync(document, products, device);
		return {...result, type: result.status, ...(result.status === SuzInternalStatus.Success ? {data: {serialNumber: result?.data?.FiscalDeviceTicket?.SerialNumber, number: result?.data?.DocumentTicket?.Number}}: {})} as SuzInternalPrintReceiptResponse;
	}

	public async printDailyReportAsync(printer: { name: string}): Promise<SuzInternalResponse> {
		const result = await this.#suzService.createDailyReportAsync(printer.name);
		return {type: result.status, suzMessages: result.suzMessages} as SuzInternalResponse;
	}

	public refreshCredentialsAsync(_: SuzCredentials): Promise<void> {
		throw new Error("Method not allowed.");
	}
}
